import LazyLoad from 'react-lazyload';
import IndexLayout from '../layouts';
import Wrapper from '../components/Wrapper';
import SiteNav from '../components/header/SiteNav';
import ReactCompareImage from 'react-compare-image';
import Spinner from '../../src/components/ui/Spinner';

import { graphql } from 'gatsby';
import Img from 'gatsby-image';

import { SiteHeader, outer, inner, SiteMain } from '../styles/shared';
import * as React from 'react';
import { css } from '@emotion/core';
import { PostFullHeader, PostFullTitle, NoImage, PostFull } from '../templates/post';
import { PostFullContent } from '../components/PostContent';
import Footer from '../components/Footer';
import Helmet from 'react-helmet';
import styled from '@emotion/styled';

//import ShareButtons from '../components/ShareButtons';
import MoreGoodies from '../components/more-goodies/MoreGoodies';
import BackToTop from '../components/BackToTop';

export const query = graphql`
  query {
    file(relativePath: { eq: "covers/light-leaks.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1800) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

interface CompareWrapperProps {
  thin?: boolean;
}

const CompareWrapper = styled.div<CompareWrapperProps>`
  max-width: ${(props) => (props.thin ? '750px' : '100%')};
  margin: 0 auto 3rem;
`;

class RerenderCompare extends React.Component {
  t1;
  state = { delayOver: false };

  componentDidMount() {
    this.t1 = setTimeout(() => {
      this.setState({ delayOver: true });
    }, 500);
  }

  componentWillUnmount() {
    clearTimeout(this.t1);
  }

  render() {
    if (!this.state.delayOver) {
      return <Spinner />;
    }
    return this.props.children();
  }
}

const ThumbWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  overflow: hidden;
  grid-gap: 0.75rem;

  img {
    object-fit: cover;
    height: 200px;
    width: 100%;
  }
  a {
    box-shadow: none;

    color: #382757;
    transition: color 0.25s ease-out;
  }
  p {
    margin-bottom: 0;
  }

  @media (max-width: 650px) {
    grid-template-columns: 1fr 1fr;

    img {
      object-fit: fill;
    }
  }
`;

const PageTemplate = css`
  .header-image {
    margin-bottom: 4.5rem;
    box-shadow: 0 10px 20px -16px #382757;
  }

  .site-main {
    background #fff;
    padding-bottom: 4vw;
  }

  .post-content {
    max-width: 1000px;
    margin: 0 auto;
  }
  .post-content .large-key .key {
    font-size: 3rem;
    padding: 15px;
    margin-top: 20px;
    display: inline-block;
    border-radius: 6px;
  }
  .large-key {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    margin-top: 4rem;
  }

  @media(max-width: 400px) {
    .large-key {
      grid-template-columns: 1fr 1fr;
    }
  }

  h2::before {
    display: none !important;
  }
`;

const LightLeaks: React.FunctionComponent = ({ data }) => (
  <IndexLayout>
    <Helmet>
      <title>22 Free Light Leaks for Photoshop | Giggster Guide</title>
      <meta
        name="description"
        content="Here's a free collection of light leak images that you can use as overlay in Photoshop to add realistic-looking light leaks to your photos for a vintage effect."
      />

      <meta property="og:site_name" content="Giggster Guide" />
      <meta property="og:type" content="article" />
      <meta property="og:title" content="22 Free Light Leaks for Photoshop" />
      <meta
        property="og:description"
        content="Here's a free collection of light leak images that you can use as overlay in Photoshop to add realistic-looking light leaks to your photos for a vintage effect."
      />
      <meta property="og:url" content="https://giggster.com/guide/light-leaks/" />
      <meta property="og:image" content="https://giggster.com/guide/covers/light-leaks.jpg" />
      <meta property="article:publisher" content="https://www.facebook.com/giggsterHQ" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="22 Free Light Leaks for Photoshop" />
      <meta
        name="twitter:description"
        content="Here's a free collection of light leak images that you can use as overlay in Photoshop to add realistic-looking light leaks to your photos for a vintage effect."
      />
      <meta name="twitter:url" content="https://giggster.com/guide/light-leaks/" />
      <meta name="twitter:image" content="https://giggster.com/guide/covers/light-leaks.jpg" />
      <meta name="twitter:site" content="@Giggster" />
      <meta name="twitter:creator" content="@Giggster" />
    </Helmet>
    <Wrapper css={PageTemplate}>
      <header css={[outer, SiteHeader]}>
        <div css={inner}>
          <SiteNav />
        </div>
      </header>
      <main id="site-main" className="site-main" css={[SiteMain, outer]}>
        <article className="post page" css={[PostFull, NoImage]}>
          <PostFullHeader>
            <PostFullTitle>22 Free Light Leaks for Photoshop</PostFullTitle>
            <h2 className="b3">
              Give your photos that vintage look by using one of these light leaks as an overlay
            </h2>
            <Img
              className="header-image"
              fluid={data.file.childImageSharp.fluid}
              alt="Showing off some of the free light leaks on real photos"
            />
            {/*<ShareButtons
              title={'22 Free Light Leaks for Photoshop'}
              slug={'/light-leaks/'}
              imgSrc={'covers/light-leaks.jpg'}
            />*/}
          </PostFullHeader>

          <PostFullContent className="post-full-content">
            <div className="post-content">
              <p>
                <a href="#the-light-leaks">Jump right to the light leaks</a>. Light leaks are
                imperfections seen in photos taken with old film cameras. It happens when some light
                can make it into the camera through a small gap in the camera chamber and
                over-expose the film in specific areas that look like streaks.
              </p>
              <p>
                Obviously at first light leaks were undesirable, but now in our world of digital
                photography and pixel-perfection, we've come to appreciate how imperfections can add
                life and soul to photographs. That's a little bit similar to how vinyls are still
                appreciated today.
              </p>
              <p>
                So, how do we go about adding realistic-looking light leaks to our images to get
                that vintage feel? Easy, just overlay an image of a light leak on top of the image
                in a retouching software like Photoshop.
              </p>
              <p>
                To help with that, I've prepared a collection of 22 light leaks that you can use for
                free right away. Some of them were created in-camera using a digital camera without
                a lens and exposing the sensor to different lights, and others were created manually
                using Photoshop based on what real light leaks look like.
              </p>
              Below you can see an example of what these light leaks look like:
              <h3 className="text-center">Light Leak Usage Example</h3>
              <CompareWrapper>
                <RerenderCompare>
                  {() => (
                    <ReactCompareImage
                      sliderPositionPercentage={0.5}
                      leftImage="/guide/images/light-leaks/examples/light-leak-before2.jpg"
                      rightImage="/guide/images/light-leaks/examples/light-leak-after2.jpg"
                      sliderLineWidth={4}
                      skeleton={<Spinner />}
                    />
                  )}
                </RerenderCompare>
              </CompareWrapper>
              
              <hr />
              
              <section className="info-box">
                <h3 className="text-center">Using the Light Leaks</h3>

                <p className="text-center">
                  I <a href="/guide/retouching/using-textures/">wrote up a post</a> about how to use
                  texture overlays in Photoshop. The same principles apply to using light leak
                  overlays. You'll add the light leak as a new layer on top and then change the
                  layer blend mode to something like <strong>Screen</strong> or{' '}
                  <strong>Color Dodge</strong>
                </p>
              </section>
              <section className="info-box">
                <h3 className="text-center">Important Tip: Preserving Quality</h3>

                <p className="text-center">
                  I found that if I drag a light leak directly inside of my desired file in
                  Photoshop, the size can get compressed and sizing it back up makes it lose its
                  original quality, which can show up as banding. To avoid that, open the light leak
                  as a separate file and then copy and paste it as a new layer in the file where it
                  should go.
                </p>
              </section>
              <hr />
              <h2 name="the-light-leaks" className="text-center">
                Free Light Leaks
              </h2>
              <p>
                Below you can see thumbnails for the 22 light leaks. Just click on any of the
                thumbnails to download a full high resolution version of the light leak.
              </p>
              <p>
                Some of them have a vertical version available that makes them ideal for shots that
                are in portrait orientation. For those, just click the <strong>vertical</strong>{' '}
                link.
              </p>
              <ThumbWrapper>
                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-1.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-1.jpg" alt="Light leak 1" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-1-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-2.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-2.jpg" alt="Light leak 2" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-2-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-15.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-15.jpg" alt="Light leak 15" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-15-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-4.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-4.jpg" alt="Light leak 4" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-5.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-5.jpg" alt="Light leak 5" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-6.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-6.jpg" alt="Light leak 6" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-7.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-7.jpg" alt="Light leak 7" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-8.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-8.jpg" alt="Light leak 8" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-9.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-9.jpg" alt="Light leak 9" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-10.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-10.jpg" alt="Light leak 10" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-11.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-11.jpg" alt="Light leak 11" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-12.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-12.jpg" alt="Light leak 12" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-13.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-13.jpg" alt="Light leak 13" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-14.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-14.jpg" alt="Light leak 14" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <a
                    target="_blank"
                    download
                    href="/guide/images/light-leaks/full/light-leak-3.jpg"
                    rel="noreferrer noopener"
                  >
                    <img src="/guide/images/light-leaks/light-leak-3.jpg" alt="Light leak 3" />
                  </a>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-16.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-16.jpg" alt="Light leak 16" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-16-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-17.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-17.jpg" alt="Light leak 17" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-17-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-18.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-18.jpg" alt="Light leak 18" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-18-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-19.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-19.jpg" alt="Light leak 19" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-19-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-20.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-20.jpg" alt="Light leak 20" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-20-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-21.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-21.jpg" alt="Light leak 21" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-21-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>

                <LazyLoad height={200}>
                  <div className="text-center">
                    <a
                      target="_blank"
                      download
                      href="/guide/images/light-leaks/full/light-leak-22.jpg"
                      rel="noreferrer noopener"
                    >
                      <img src="/guide/images/light-leaks/light-leak-22.jpg" alt="Light leak 22" />
                    </a>
                    <p>
                      <a
                        href="/guide/images/light-leaks/full/light-leak-22-vertical.jpg"
                        target="_blank"
                        download
                        rel="noreferrer noopener"
                      >
                        vertical
                      </a>
                    </p>
                  </div>
                </LazyLoad>
              </ThumbWrapper>
            </div>
          </PostFullContent>
        </article>
        <MoreGoodies slug="light-leaks" />
      </main>
      <BackToTop />
      <Footer />
    </Wrapper>
  </IndexLayout>
);

export default LightLeaks;
